import React, { useState, useEffect } from "react"
import fuzzysort from "fuzzysort"

import { Idea } from "../../model/types"
import { useNana } from "../hooks/useNana"
import IdeaBox from "./idea_box"
import FuzzyFinder, { FuzzyOptions } from "./fuzzy_finder"
import HighlightText from "../../services/highlight_text"
import { NavigationHistoryState } from "../routes/home/mosaic"

type Result = Fuzzysort.KeysResult<Idea> | { obj: Idea }

const highlight = (result: Result) =>
  (result as Fuzzysort.KeysResult<Idea>)[0].highlight("<b>", "</b>")

const FuzzyIdeaFinder = ({
  title,
  showNearbyOnOpen,
  onChoose,
}: {
  title?: string
  showNearbyOnOpen: boolean
  onChoose: (item: Idea) => void
}) => {
  const { model } = useNana()

  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    const a = () => setIsOpen(true)
    window.addEventListener("open-fuzzy-finder", a)

    return () => {
      window.removeEventListener("open-fuzzy-finder", a)
    }
  }, [])

  if (!isOpen) return null

  const options: FuzzyOptions<Result> = {
    title: title,

    placeholder: "Search ideas...",

    fetchItems: (filter: string): Result[] => {
      if (filter === "") {
        if (showNearbyOnOpen)
          return model
            .getNearestIdeasTo(NavigationHistoryState.get_open_location())
            .map((idea) => ({ obj: idea }))
        else return []
      }
      const results = fuzzysort.go(filter, model.getIdeasWithTitles(), {
        limit: 20,
        threshold: -1000,
        keys: ["title", "memo"],
      })
      return Array.from(results)
    },

    renderItem: (result: Result) => {
      return (
        <>
          <IdeaBox idea={result.obj} shadow={false} />
          {result.obj.type === "goal" && (
            <span className="shrink-0 rounded-full bg-stone-900/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-500 dark:bg-white/[0.08] dark:text-stone-400">
              Goal
            </span>
          )}
          <div className="min-w-0 flex-1 truncate text-[14px] font-normal text-stone-800 dark:text-stone-200">
            {result["score"] !== undefined && (
              <HighlightText text={highlight(result)} />
            )}
            {result["score"] === undefined && result.obj.title}
          </div>
        </>
      )
    },

    getIdForItem: (item: Result) => {
      return item.obj.id
    },

    chooseItem: (item: Result) => {
      onChoose(item.obj)
    },

    close: () => setIsOpen(false),
  }

  return <FuzzyFinder {...options} />
}

export default FuzzyIdeaFinder
